import type { IKeyObjective } from 'legacy/lut/classes/models/partner360/IFormOptionsJBP'
import type { IKIBusinessPriority } from 'legacy/lut/features/partner360/jbpSettings/models/kiBusinessPriority'

import type { IMappedGeoMappingItem } from 'partner360/components/filters'
import { getQualityBand } from 'partner360/components/qualityIndicator'
import type { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import type {
  CockpitPeriodKey,
  ICockpitKeyInitiative,
  ICockpitPartnerData,
  ICockpitPlanningPeriod,
  IEnrichedCockpitFinSobRow,
} from '../../model/types'

import { getCockpitInitiativesPlanQuality } from './getCockpitInitiativesPlanQuality'
import type { ICockpitInitiativeHierarchyRow } from './types'

interface IParams {
  initiatives: ICockpitKeyInitiative[]
  finSobRows: IEnrichedCockpitFinSobRow[]
  partners: ICockpitPartnerData[]
  geoMappingItems: IMappedGeoMappingItem[]
  businessPriorities: IKIBusinessPriority[]
  keyObjectives?: readonly IKeyObjective[]
  offering: string
  fYear: number
  currentFQuarter: number
  fQuarterActualsPercentage: number
  selectedMetric: ActualsValueTypes
  selectedCurrency: string
  selectedPeriods: CockpitPeriodKey[]
  planningPeriod: ICockpitPlanningPeriod | null
  isLocalCurrency: boolean
  selectedCategory?: string | null
}

const getFinancialCoverageScore = (initiativeIncremental: number, financialIncremental: number) => {
  if (financialIncremental < 0) {
    return null
  }

  if (!financialIncremental) {
    return 0
  }

  return Math.round(
    Math.min(Math.max((initiativeIncremental / financialIncremental) * 100, 0), 100)
  )
}

const groupByPartnerId = <T>(items: T[], getPartnerId: (item: T) => string) => {
  const itemsByPartnerId = new Map<string, T[]>()

  items.forEach((item) => {
    const partnerId = getPartnerId(item)
    const group = itemsByPartnerId.get(partnerId)

    if (group) {
      group.push(item)
    } else {
      itemsByPartnerId.set(partnerId, [item])
    }
  })

  return itemsByPartnerId
}

/**
 * Resolves the existing Initiatives quality calculation for a hierarchy aggregate, on first use.
 * The hierarchy holds thousands of aggregates while only the opened ones are drawn, so each is
 * calculated when a cell or the export asks for it and kept for the next ask.
 * The hierarchy row supplies the initiative scope; its partner ids scope the financial target.
 * Period groups additionally use the row's already allocated Incremental amount, so a Q1 row does
 * not accidentally display the full selected-period initiative plan.
 */
export const getCockpitInitiativeHierarchyQuality = ({
  initiatives,
  finSobRows,
  partners,
  ...qualityParams
}: IParams) => {
  const initiativeById = new Map(initiatives.map((initiative) => [initiative.EntryId, initiative]))
  const finSobRowsByPartnerId = groupByPartnerId(finSobRows, ({ partnerId }) => partnerId)
  const partnersById = groupByPartnerId(partners, ({ PartnerId }) => PartnerId)
  const qualityByScope = new Map<string, ReturnType<typeof getCockpitInitiativesPlanQuality>>()
  const qualityByRowId = new Map<string, ReturnType<typeof getCockpitInitiativesPlanQuality>>()

  return (row: ICockpitInitiativeHierarchyRow) => {
    const rowQuality = qualityByRowId.get(row.rowId)

    if (rowQuality) {
      return rowQuality
    }

    const scopedInitiatives = row.initiativeEntryIds
      .map((entryId) => initiativeById.get(entryId))
      .filter((initiative): initiative is ICockpitKeyInitiative => Boolean(initiative))
    const partnerIds = [...new Set(scopedInitiatives.map(({ PartnerId }) => PartnerId))]
    const sortedIds = [...row.initiativeEntryIds].sort((a, b) => a - b).join(',')
    const cacheKey = [sortedIds, String(row.incremental ?? 'null')].join('::')
    let quality = qualityByScope.get(cacheKey)

    if (!quality) {
      const calculated = getCockpitInitiativesPlanQuality({
        ...qualityParams,
        initiatives: scopedInitiatives,
        rows: partnerIds.flatMap((partnerId) => finSobRowsByPartnerId.get(partnerId) ?? []),
        partners: partnerIds.flatMap((partnerId) => partnersById.get(partnerId) ?? []),
      })
      const initiativeIncremental = row.incremental ?? 0
      const financialIncremental = calculated.financialPlanCoverage.incrementalPlanSum
      const score =
        calculated.financialPlanCoverage.score === null
          ? null
          : getFinancialCoverageScore(initiativeIncremental, financialIncremental)

      quality = {
        ...calculated,
        financialPlanCoverage: {
          ...calculated.financialPlanCoverage,
          ...(score === null
            ? { score: null, band: null }
            : { score, band: getQualityBand(score) }),
          incrementalInitiativesSum: initiativeIncremental,
        },
      }
      qualityByScope.set(cacheKey, quality)
    }

    qualityByRowId.set(row.rowId, quality)

    return quality
  }
}

export type TCockpitInitiativeHierarchyQuality = ReturnType<typeof getCockpitInitiativesPlanQuality>
